import React, { useState,useEffect, useRef } from "react";
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from "axios";
import AdminPage from "./AdminPage";

import { SERVERURL } from "../../ServerUrl";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import HomeTopBar from "../HomeTopBar/HomeTopBar";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from "sweetalert2";
const allowedFileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/JPEG', 'image/JPG', 'image/PNG'];

const schema = Yup.object().shape({
  type: Yup.string().oneOf(['AI', 'DEFI', 'Metaverse', 'NFTs', 'Infrastructure', 'Gaming']).required('Type/Category is required'),
  title: Yup.string().max(30, 'Title cannot exceed 30 characters').required('Title is required'),
  shortDescription: Yup.string().max(50, 'Short description cannot exceed 50 characters').required('Short description is required'),
  description: Yup.string().required('Description is required'),
  ytVideo: Yup.string().url('Please enter a valid YouTube URL').nullable(),
  readTime: Yup.string().required('Read time is required'),
  coverImage: Yup.mixed().required('Cover image is required').test('fileType', 'Only JPG, JPEG, PNG, and SVG images are allowed', (file) => file && allowedFileTypes.includes(file.type)),
  references: Yup.array().of(Yup.string().required('Reference is required').url('Please enter a valid URL')).nullable(),
});

const COPY = {
  title: "Add Blog",
  subtitle:
    "Provide the details of your Blog. Ensure all fields are filled out accurately to share your Blog with the world.",
};

const CreateBlog = () => {
  const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
  return (
    <AdminPage title={COPY.title} subtitle={COPY.subtitle}>
      <div className="admin-card" style={{ maxWidth: "820px" }}>
            <Formik
          initialValues={{
            type: '',
            title: '',
            shortDescription: '',
            description: '',
            ytVideo: '',
            readTime: '',
            coverImage: null,
            references: [],
          }}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            const formData = new FormData();
          
            // Append simple fields
            formData.append('title', values.title);
            formData.append('shortDescription', values.shortDescription);
            formData.append('description', values.description);
            formData.append('ytVideo', values.ytVideo);
            formData.append('type', values.type);
            formData.append('readTime', values.readTime);
          
            // Handle coverImage upload
            if (values.coverImage) {
              formData.append('coverImage', values.coverImage);
            }
          
            // Handle references array
            const referencesJSON = JSON.stringify(values.references);
            formData.append('references', referencesJSON);
          
            // Printing data for debugging
            formData.forEach((value, key) => {
              console.log(`${key}: ${value}`);
            });
          
            // API call to /AddBlog
            const token = localStorage.getItem('token');
            
            try {
              if (token) {
                const headers = {
                  'Authorization': `Bearer ${token}`,
                };
          
                const response = await axios.post(
                  `${SERVERURL}/api/v1/AddBlog`,
                  formData,
                  {
                    headers: headers,
                    onUploadProgress: (progressEvent) => {
                      const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    
                      setProgress(progress);
                      setUploading(true);
                    },
                  }
                );
          
                if (response.status === 200) {
                  Swal.fire({
                    icon: "success",
                    title: "Uploaded Successfully",
                    text: response.data.message,
                  });
                } else {
                  Swal.fire({
                    icon: "error",
                    title: "An error occurred",
                    text: response.data.message,
                  });
                }
              } else {
                Swal.fire({
                  icon: "error",
                  title: "Sign in to upload Blog",
                  text: "Guest User can't upload Blog.",
                });
              }
            } catch (error) {
              Swal.fire({
                icon: "error",
                title: "An error occurred",
                text: "Blog Not Uploaded.",
              });
            } finally {
              setUploading(false);
              setSubmitting(false); // Reset submitting state to false
            }
          }}
          
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
                {/* Title Field */}
                <div className='mb-3'>
                    <label className="admin-label">Title <span className="admin-error">*</span></label>
                    <Field type="text" name="title" className="admin-input" />
                    <ErrorMessage name="title" component="div" className="admin-error" />
                </div>
                <div className="mt-4 mb-4 flex flex-col md:flex-row md:mb-0">
                    {/* Chain Field */}
                    <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                        <label className="admin-label">Category <span className="admin-error">*</span></label>
                        <Field as="select" name="type" className="admin-input">
                            <option value="">Select a category</option>
                            <option value="AI">AI</option>
                            <option value="DEFI">DEFI</option>
                            <option value="Metaverse">Metaverse</option>
                            <option value="NFTs">NFTs</option>
                            <option value="Infrastructure">Infrastructure</option>
                            <option value="Gaming">Gaming</option>
                        </Field>
                        <ErrorMessage name="type" component="div" className="admin-error" />
                    </div>

                </div>
                {/* Short Description Field */}
                <div className='mb-3'>
                    <label className="admin-label">Short Description <span className="admin-error">*</span></label>
                    <Field type="text" name="shortDescription" className="admin-input" />
                    <ErrorMessage name="shortDescription" component="div" className="admin-error" />
                </div>

                {/* Description Field */}
                <div className='mb-3'>
                    <label className="admin-label">Description <span className="admin-error">*</span></label>
                    <Field name="description">
                        {({ field }) => (
                            <ReactQuill
                                value={field.value}
                                onChange={field.onChange(field.name)}
                                className="admin-input"
                            />
                        )}
                    </Field>
                    <ErrorMessage name="description" component="div" className="admin-error" />
                </div>

                {/* YouTube Link */}
                <div className='mb-3'>
                    <label className="admin-label">YouTube Video Link</label>
                    <Field type="url" name="ytVideo" className="admin-input" />
                    <ErrorMessage name="ytVideo" component="div" className="admin-error" />
                </div>

                {/* Read Time */}
                <div className='mb-3'>
                    <label className="admin-label">Read Time <span className="admin-error">*</span></label>
                    <Field type="text" name="readTime" className="admin-input" />
                    <ErrorMessage name="readTime" component="div" className="admin-error" />
                </div>

                {/* Cover Image */}
                <div className='mb-3'>
                    <label className="admin-label">Cover Image <span className="admin-error">*</span></label>
                    <input
                        type="file"
                        name="coverImage"
                        onChange={(event) => {
                            setFieldValue("coverImage", event.currentTarget.files[0]);
                        }}
                        className="admin-input"
                    />
                    <ErrorMessage name="coverImage" component="div" className="admin-error" />
                </div>

                {/* References */}
                
                <FieldArray name="references">
                {({ remove, push }) => (
                  <div className='my-3'>
                    {values.references && values.references.length > 0 && values.references.map((reference, index) => (
                      <div key={index} className="admin-subcard">
                        <div className="admin-field">
                          <label className="admin-label">Reference URL <span className="admin-error">*</span></label>
                          <Field
                            name={`references.${index}`}
                            type="url"
                            className="admin-input"
                          />
                          <ErrorMessage name={`references.${index}`} component="div" className="admin-error" />
                        </div>

                        <button type="button" onClick={() => remove(index)}
                          className="admin-action admin-action-delete">
                          Remove
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => push('')}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mb-4"
                    >
                      Add Reference
                    </button>
                  </div>
                )}
              </FieldArray>


              {/* Submit Button */}
              <div className="flex items-center mt-3">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="admin-btn"
                >
                    Submit
                </button>
              </div>
            </Form>
        )}
        </Formik>
        {uploading && (
          <div className="modddd">
                    <div className="coontent">                  
                      <p>Uploading: {progress}%</p>
                    <div className="w-full bg-gray-200 rounded">
                        <div className="bg-blue-500 text-xs leading-none py-1 text-center text-white rounded" style={{ width: `${progress}%` }}>
                            
                        </div>
                    </div>
                </div>
                </div>
            )}
      </div>
    </AdminPage>
  )
}

export default CreateBlog;